import { useState, useRef, useEffect } from 'react'

export type WordTiming = {
  word: string
  offset_ms: number
  duration_ms: number
  text_offset: number
  word_length: number
}

/**
 * Manages text reveal for avatar speech.
 * - startFallback(text): shows full text immediately — used when no word timings arrive
 * - startWithTimings(fullText, words, startTimeMs): drives reveal via requestAnimationFrame
 *   using performance.now() as the clock. startTimeMs is performance.now() captured at the
 *   moment speech_started fires. Using performance.now() (not audioEl.currentTime) avoids
 *   jumps/resets that occur when LiveKit attaches new audio tracks.
 * - complete(): snap to full text immediately
 * - reset(): clear all state
 */
export function useTextStream() {
  const [displayed, setDisplayed] = useState('')
  const rafRef = useRef<number | null>(null)
  const fullTextRef = useRef('')

  function clearTimers() {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }

  function startFallback(text: string) {
    clearTimers()
    fullTextRef.current = text
    setDisplayed(text)
  }

  function startWithTimings(
    fullText: string,
    wordTimings: WordTiming[],
    startTimeMs: number,
  ) {
    clearTimers()
    fullTextRef.current = fullText
    // Clear the previous segment's text. Without this, a shorter new segment
    // would render nothing (the old length-comparison guard) or overlap with
    // the previous final snap.
    setDisplayed('')

    if (wordTimings.length === 0) {
      setDisplayed(fullText)
      return
    }

    const last = wordTimings[wordTimings.length - 1]
    const endMs = last.offset_ms + last.duration_ms + 100

    function tick() {
      const elapsed = performance.now() - startTimeMs

      let revealUpTo = 0
      for (const { offset_ms, text_offset, word_length } of wordTimings) {
        if (elapsed >= offset_ms) {
          revealUpTo = Math.max(revealUpTo, text_offset + word_length)
        }
      }

      if (revealUpTo > 0) {
        setDisplayed(fullText.slice(0, revealUpTo))
      }

      // Stop the loop once all words are revealed — complete() handles the final snap
      if (revealUpTo >= fullText.length || elapsed >= endMs) {
        rafRef.current = null
        return
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
  }

  function complete() {
    clearTimers()
    setDisplayed(fullTextRef.current)
  }

  function reset() {
    clearTimers()
    setDisplayed('')
    fullTextRef.current = ''
  }

  /** Pre-seed fullTextRef so complete() snaps to the right text even if
   *  startFallback / startWithTimings were never called (e.g. fallback timer fires
   *  without any speech_started event). Does NOT change displayed text. */
  function preload(text: string) {
    fullTextRef.current = text
  }

  useEffect(() => () => clearTimers(), [])

  return { displayed, startFallback, startWithTimings, complete, reset, preload }
}
